import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Chip } from "../components/ui-bits";
import { useBrandPermissions } from "../lib/brand";
import { ArrowLeft, Edit3 } from "lucide-react";
import { STAGE_TONE, useBrandMembers } from "../components/sampling/common";
import { StageTimeline } from "../components/sampling/StageTimeline";
import { ReadinessList } from "../components/sampling/ReadinessList";
import { BomEditor } from "../components/sampling/BomEditor";
import { ProductionDetailsForm } from "../components/sampling/ProductionDetailsForm";
import { ProductionPanel, VersionHistory } from "../components/sampling/ProductionPanel";

const ANCHOR = { bom: "sampling-bom", construction: "sampling-construction", main: "sampling-pricing" };

export default function GarmentDevelopmentPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit } = useBrandPermissions();
  const members = useBrandMembers();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get(`/sampling/garments/${id}`).then(r => setD(r.data)).catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Capo non trovato")), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true); setErr(""); setMsg("");
    try { const r = await fn(); if (r?.data?.garment) setD(r.data); else await load(); if (ok) setMsg(ok); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita"); }
    setBusy(false);
  };
  const g = d?.garment;
  const saveGarment = (patch, ok = "Salvato") => run(() => api.put(`/garments/${id}`, { name: g.name, code: g.code, ...patch }), ok);
  const stageLabel = (k) => d?.stages.find(s => s.key === k)?.label || k || "—";
  const complete = (c) => {
    if (ANCHOR[c.section]) document.getElementById(ANCHOR[c.section])?.scrollIntoView({ behavior: "smooth", block: "center" });
    else nav(`/collections-garments?tab=garments&open=${id}&section=${c.section}&return=/garments/${id}/development`);
  };

  if (err && !d) return <div className="text-sm text-red-600" data-testid="dev-error">{err}</div>;
  if (!d) return <div className="text-sm opacity-60">Caricamento…</div>;
  return (
    <div data-testid="garment-development-page">
      <button onClick={() => nav("/collections-garments?tab=garments")} className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-2 mb-6 uppercase tracking-widest" data-testid="dev-back"><ArrowLeft size={14}/> Capi</button>
      <PageHeader eyebrow={`${g.code} · Sviluppo campionario`} title={g.name}
        action={<div className="flex items-center gap-3">
          <Chip tone={STAGE_TONE[g.sample_stage]}>{stageLabel(g.sample_stage)}</Chip>
          {canEdit && <button onClick={() => nav(`/collections-garments?tab=garments&open=${id}&return=/garments/${id}/development`)} className="btn-ghost inline-flex items-center gap-2" data-testid="dev-edit-garment"><Edit3 size={14}/> Scheda capo</button>}
        </div>}/>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div><label className="label-caps block mb-2">Responsabile</label>
          <select disabled={!canEdit || busy} className="input-dark" value={g.responsible_id || ""} onChange={e => saveGarment({ responsible_id: e.target.value || null }, "Responsabile aggiornato")} data-testid="dev-responsible">
            <option value="">Nessuno</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
        <div><label className="label-caps block mb-2">Scadenza campione</label>
          <input disabled={!canEdit || busy} type="date" className="input-dark" value={g.sample_due_date || ""} onChange={e => saveGarment({ sample_due_date: e.target.value || null }, "Scadenza aggiornata")} data-testid="dev-due-date"/></div>
        <div className="text-xs opacity-70 pt-7">Fase capo: <b>{g.dev_phase}</b> (si aggiorna con la timeline)</div>
      </div>
      {err && <div className="text-sm text-red-600 mb-4" data-testid="dev-error">{err}</div>}
      {msg && <div className="text-sm text-green-700 mb-4" data-testid="dev-msg">{msg}</div>}
      <StageTimeline detail={d} stageLabel={stageLabel} canEdit={canEdit} busy={busy}
        onStage={(stage, note) => run(() => api.post(`/sampling/garments/${id}/stage`, { stage, note: note || null }), stage === "approved" ? "Campione approvato" : "Fase aggiornata")}
        onNote={(note, file_ids) => run(() => api.post(`/sampling/garments/${id}/notes`, { note, file_ids }), "Nota salvata")}/>
      <ReadinessList detail={d} members={members} onComplete={complete}/>
      <BomEditor detail={d} canEdit={canEdit} busy={busy} onSave={(p) => saveGarment(p, "Distinta salvata")}/>
      <ProductionDetailsForm detail={d} canEdit={canEdit} busy={busy} onSave={(p) => saveGarment(p)}/>
      <ProductionPanel detail={d} canEdit={canEdit} busy={busy} onChanged={load}
        onGenerate={() => window.confirm("Generare la scheda produzione con i dati definitivi? Il capo passerà alla fase Produzione.") && run(() => api.post(`/sampling/garments/${id}/production-sheet`), "Scheda produzione generata")}/>
      <VersionHistory detail={d} canEdit={canEdit} busy={busy}
        onRestore={(v) => run(() => api.post(`/sampling/garments/${id}/versions/${v.id}/restore`), `Versione ${v.version} ripristinata`)}/>
    </div>
  );
}
